import React, { useEffect, useMemo, useRef, useState } from 'react';

const DENOMINATIONS = [
  { value: 1000, label: '₱1,000' },
  { value: 500, label: '₱500' },
  { value: 200, label: '₱200' },
  { value: 100, label: '₱100' },
  { value: 50, label: '₱50' },
  { value: 20, label: '₱20' },
  { value: 10, label: '₱10' },
  { value: 5, label: '₱5' },
  { value: 1, label: '₱1' }
];

const buildInitialState = (initialValues = {}) => {
  const base = {};
  DENOMINATIONS.forEach(({ value }) => {
    base[String(value)] = Number(initialValues[String(value)] || 0);
  });
  return base;
};

export const DenominationGrid = ({ onChange, readOnly = false, initialValues, columns = 1 }) => {
  const [quantities, setQuantities] = useState(() => buildInitialState(initialValues));
  const onChangeRef = useRef(onChange);

  const initialValuesSignature = useMemo(
    () => JSON.stringify(initialValues || {}),
    [initialValues]
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setQuantities(buildInitialState(initialValues));
  }, [initialValuesSignature, initialValues]);

  const total = useMemo(() => {
    return DENOMINATIONS.reduce((sum, denomination) => {
      return sum + denomination.value * Number(quantities[String(denomination.value)] || 0);
    }, 0);
  }, [quantities]);

  useEffect(() => {
    onChangeRef.current?.(quantities, total);
  }, [quantities, total]);

  const updateQty = (value, quantity) => {
    const sanitized = Math.max(0, Number(quantity || 0));
    setQuantities((prev) => ({
      ...prev,
      [String(value)]: sanitized
    }));
  };

  return (
    <div className="space-y-2">
      <div className={columns === 2 ? 'grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-x-4' : 'space-y-2'}>
      {DENOMINATIONS.map((denomination) => {
        const key = String(denomination.value);
        const quantity = Number(quantities[key] || 0);
        const lineTotal = denomination.value * quantity;

        return (
          <div key={key} className="grid grid-cols-3 items-center gap-2">
            <span className="text-sm font-medium text-gray-700">{denomination.label}</span>
            <input
              type="number"
              min="0"
              step="1"
              value={quantity}
              readOnly={readOnly}
              onChange={(event) => updateQty(denomination.value, event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-2 py-1 text-right text-sm"
            />
            <span className="text-right text-sm font-semibold text-gray-800">₱{lineTotal.toLocaleString()}</span>
          </div>
        );
      })}
      </div>

      <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-right">
        <p className="text-xs uppercase tracking-wide text-gray-500">Total</p>
        <p className="text-xl font-bold text-gray-900">₱{total.toLocaleString()}</p>
      </div>
    </div>
  );
};

export { DENOMINATIONS };
export default DenominationGrid;
